import axios from "axios";
import { API_BASE } from "../settings/master";
import { refreshCtx, processQueue as sharedProcessQueue, triggerSessionExpired, apiAuth } from "./auth";

const ax = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 900000,
});

ax.interceptors.request.use(cfg => {
  const cid = localStorage.getItem("orgId") || "";
  if (cid) {
    if ((cfg.method || "get").toLowerCase() === "get") {
      cfg.params = cfg.params || {};
      if (!("org_id" in cfg.params)) cfg.params.org_id = cid;
    } else {
      if (cfg.data instanceof FormData) {
        if (!cfg.data.has("org_id")) cfg.data.append("org_id", cid);
      } else {
        cfg.data = cfg.data || {};
        if (!("org_id" in cfg.data)) cfg.data.org_id = cid;
      }
    }
  }
  return cfg;
});

const parseExp = v => {
  if (v == null) return null;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  if (typeof v === "string") {
    if (/^\d+$/.test(v)) return parseInt(v, 10) < 1e12 ? parseInt(v, 10) * 1000 : parseInt(v, 10);
    const ts = Date.parse(v); if (!Number.isNaN(ts)) return ts;
  }
  return null;
};
const harvestExp = r => {
  const h = (r && r.headers) || {};
  const cand = h["x-session-exp"] ?? h["x-token-exp"] ?? h["x-exp"] ?? h["x-jwt-exp"] ?? r?.data?.session_exp ?? r?.data?.token_exp;
  const t = parseExp(cand);
  if (t) localStorage.setItem("sessionExp", String(t));
};
const SKIP_401_PATHS = ["/me", "/me-info", "/my-profile", "/renew", "/auth/refresh", "/auth/learner/refresh"];
const getPath = url => {
  try { return new URL(url, API_BASE).pathname; } catch { return String(url || ""); }
};
const shouldSkip401 = url => SKIP_401_PATHS.includes(getPath(url));

ax.interceptors.response.use(
  r => { harvestExp(r); return r; },
  async err => {
    const orig = err.config;
    const status = err?.response?.status;
    const url = orig?.url;

    if (status !== 401 || orig._retry || shouldSkip401(url)) {
      return Promise.reject(err);
    }

    if (refreshCtx.isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshCtx.failedQueue.push({ resolve, reject });
      }).then(() => ax(orig)).catch(e => Promise.reject(e));
    }

    orig._retry = true;
    refreshCtx.isRefreshing = true;

    const isLearner = (window.location.pathname || "").startsWith("/learn");
    const refreshUrl = isLearner ? "/auth/learner/refresh" : "/auth/refresh";

    try {
      const r = await apiAuth.post(refreshUrl);
      harvestExp(r);
      sharedProcessQueue(null);
      return ax(orig);
    } catch (refreshErr) {
      sharedProcessQueue(refreshErr);
      triggerSessionExpired();
      return Promise.reject(refreshErr);
    } finally {
      refreshCtx.isRefreshing = false;
    }
  }
);

const stable = v => {
  if (v === null || typeof v !== "object") return JSON.stringify(v);
  if (Array.isArray(v)) return "[" + v.map(stable).join(",") + "]";
  const keys = Object.keys(v).sort();
  return "{" + keys.map(k => JSON.stringify(k) + ":" + stable(v[k])).join(",") + "}";
};
const inflight = new Map();
const keyOf = c => [
  (c.method || "get").toLowerCase(),
  c.url,
  stable(c.params || {}),
  stable(c.data || {})
].join("|");
const run = c => {
  const k = keyOf(c);
  if (inflight.has(k)) return inflight.get(k);
  const p = ax.request(c).finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
};

const api = {
  get: (url, cfg = {}) => run({ url, method: "get", ...cfg }),
  post: (url, data, cfg = {}) => run({ url, method: "post", data, ...cfg }),
  put: (url, data, cfg = {}) => run({ url, method: "put", data, ...cfg }),
  patch: (url, data, cfg = {}) => run({ url, method: "patch", data, ...cfg }),
  delete: (url, cfg = {}) => run({ url, method: "delete", ...cfg }),
  request: cfg => run(cfg),
  interceptors: ax.interceptors,
  defaults: ax.defaults
};

const isUUID = s => typeof s === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

export const fetchLinkPreview = url => api.get("/link-preview", { params: { url } });

export const getSubjects = () => api.get("/contents");
export const addSubject = payload => api.post("/contents/add", payload);
export const updateSubject = payload =>
  api.post("/contents/update", payload);
export const deleteSubject = (id) => api.post("/contents/delete", { id });
export const getSubjectName = (input) =>
  api.get("/content-name", {
    params: typeof input === "string"
      ? (input.match(/^[0-9a-f-]{36}$/i) ? { contentId: input } : { contentCode: input })
      : { contentId: input?.id, contentCode: input?.code }
  });

export const getLessons = params => {
  const p = {
    contentId: params?.contentId || params?.content_id,
    contentCode: params?.contentCode || params?.content_code,
    level: params?.level,
    examType: params?.examType,
    searchText: params?.searchText || params?.search,
    page: params?.page,
    pageSize: params?.pageSize
  };
  return api.get("/lessons", { params: p });
};

export const getLessonsAttachs = params => {
  const p = {
    contentId: params?.contentId || params?.content_id,
    contentCode: params?.contentCode || params?.content_code,
    level: params?.level,
    examType: params?.examType,
    searchText: params?.searchText || params?.search,
    page: params?.page,
    pageSize: params?.pageSize
  };
  return api.get("/lessonss", { params: p });
};

export const addLesson = payload =>
  api.post("/lessons/add", {
    lesson_name: payload.lesson_name,
    content_id: payload.content_id,
    difficult_level: payload.difficult_level,
    type_summary: payload.type_summary,
    type_summary_min: payload.type_summary_min,
    type_summary_max: payload.type_summary_max,
    exam_type: payload.exam_type,
    option_style: payload.option_style
  });
export const updateLesson = payload =>
  api.post("/lessons/update", {
    id: payload.id,
    lesson_name: payload.lesson_name,
    content_id: payload.content_id,
    difficult_level: payload.difficult_level,
    type_summary: payload.type_summary,
    type_summary_min: payload.type_summary_min,
    type_summary_max: payload.type_summary_max,
    exam_type: payload.exam_type,
    option_style: payload.option_style
  });
export const deleteLesson = id => api.post("/lessons/delete", { id });
export const getLessonName = lesson_id =>
  api.get("/lesson-name", { params: { lesson_id } });

export const fetchExamBank = params =>
  api.get("/exam-bank", {
    params: {
      contentId: params?.contentId || params?.content_id,
      contentCode: params?.contentCode || params?.content_code,
      level: params?.level,
      examType: params?.examType,
      searchText: params?.searchText || params?.search,
      page: params?.page,
      pageSize: params?.pageSize,
      lessonId: params?.lessonId || params?.lesson_id,
      type_summary: params?.type_summary
    }
  });
export const saveQuestions = payload => api.post("/questions/save", payload);
export const updateQuestions = (maybeQuestions, maybeQs) => {
  const questions = Array.isArray(maybeQuestions) ? maybeQuestions : maybeQs;
  return api.post("/questions/update-batch", { questions });
};
export const updateQuestion = payload => api.post("/questions/update-one", payload);
export const deleteQuestion = id => api.post("/questions/delete-one", { id });
export const deleteQuestions = ids => api.post("/questions/delete-many", { ids });

export const getLessonAttachments = (lessonId) =>
  api.get(`/lessons/${lessonId}/attachments`)

export const uploadKnowledge = (formData) =>
  api.post("/upload-knowledges", formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })

export const createLinkKnowledge = (payload) =>
  api.post("/upload-knowledges", payload)

export const deleteAttachment = (id) =>
  api.delete(`/lesson-attachments/${id}`)


export const uploadKnowledgeFile = (formData, cfg = {}) =>
  api.post("/upload-knowledge", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: cfg.onUploadProgress,
    ...cfg,
  });

export const uploadKnowledgeLink = payload => api.post("/upload-knowledge", payload);
export const uploadKnowledgeText = payload => api.post("/upload-knowledge", payload);
export const listAttachments = content_id =>
  api.get("/content-attachments", { params: { content_id } });
export const listKnowledge = lesson_id =>
  api.get("/list-knowledges", { params: { lesson_id } });
export const removeKnowledge = (lesson_id, id) =>
  api.post("/remove-knowledge", { lesson_id, id });
export const cleanupKnowledge = exam_id =>
  api.post("/cleanup-knowledge", { exam_id });
export const knowledgeTokenCount = exam_id =>
  api.get("/knowledge-token-count", { params: { exam_id } });
export const getKnowledgeJson = (lesson_id, attach_id) =>
  api.get("/get-knowledge-jsons", { params: { lesson_id, attach_id } });
export const saveKnowledgeJson = payload => api.post("/save-knowledge-json", payload);
export const generateFromKnowledge = payload =>
  api.post("/generate-from-knowledge", payload);
export const generateExam = payload =>
  api.post("/generate-from-knowledged", payload);
export const summarizeKnowledge = lesson_id =>
  api.post("/summarize-knowledge-v2", { lesson_id });
export const buildDownloadUrl = (path, fid) => {
  const id = fid || path;
  if (!id) return "";
  return `${API_BASE}/attachment-file/${encodeURIComponent(id)}`;
};
export const upsertCustomQuestions = payload => api.post("/upsert-custom-questions", payload);
export const deleteCustomQuestions = payload => api.post("/delete-custom-questions", payload);
export const getClasses = () => api.get("/get-classes");
export const saveClass = payload => api.post("/save-class", payload);
export const deleteClass = class_id => api.post("/delete-class", { class_id });
export const getClassMembers = (class_id, filters = {}) => api.get("/get-class-members", { params: { class_id, ...filters } });
export const addClassMember = payload => api.post("/add-class-member", payload);
export const removeClassMember = (class_id, learner_id) => api.post("/remove-class-member", { class_id, learner_id });
export const getClassCourseMembers = (class_id, course_id) => api.get("/get-class-course-members", { params: { class_id, course_id } });
export const addClassCourseMember = (payload) => api.post("/add-class-course-member", payload);
export const removeClassCourseMember = (payload) => api.post("/remove-class-course-member", payload);
export const getClassCourses = class_id => api.get("/get-class-courses", { params: { class_id } });
export const mapClassCourses = (class_id, course_ids) => api.post("/map-class-courses", { class_id, course_ids });
export const unmapClassCourses = (class_id, course_ids) => api.post("/unmap-class-courses", { class_id, course_ids });
export const getCourseTestApprovals = params => api.get("/get-course-test-approvals", { params })
export const getMyCourseTestApprovals = param => api.get("/get-my-course-test-approvals", { params: param })
export const updateCourseTestApproval = (id, status) => api.patch(`/update-course-test-approval/${id}`, { status })
export const getMyCourses = (params = {}) =>
  api.get("/get-my-courses", {
    params: {
      search: params.search,
      status: params.status,
    },
  });

export const getCourses = (params = {}) =>
  api.get("/get-courses", { params: { search: params.search, status: params.status } });
export const saveCourse = payload => api.post("/save-course", payload);
export const toggleCourse = (course_id, active) => api.post("/toggle-course", { course_id, active });
export const deleteCourse = course_id => api.post("/delete-course", { course_id });
export const getCourseDetail = course_id => api.get("/course-detail", { params: { course_id } });

export const getAppCourseDetail = course_id => api.get("/app-course-detail", { params: { course_id } });
export const getAppCourseContents = course_id => api.get("/app-course-contents", { params: { course_id } });

export const getCourseTests = course_id =>
  api.get("/get-course-tests", { params: { course_id } });
export const getAvailableTests = params =>
  api.get("/get-available-tests", { params });
export const mapCourseTests = (course_id, test_ids) =>
  api.post("/map-course-tests", { course_id, test_ids });
export const unmapCourseTests = (course_id, test_ids) =>
  api.post("/unmap-course-tests", { course_id, test_ids });
export const reorderCourseTests = (course_id, ordered_test_ids) =>
  api.post("/reorder-course-tests", { course_id, ordered_test_ids });

export const getCourseContents = course_id =>
  api.get("/get-course-contents", { params: { course_id } });
export const mapCourseContents = (course_id, content_ids) =>
  api.post("/map-course-contents", { course_id, content_ids });
export const unmapCourseContents = (course_id, content_ids) =>
  api.post("/unmap-course-contents", { course_id, content_ids });
export const reorderCourseContents = (course_id, ordered_content_ids) =>
  api.post("/reorder-course-contents", { course_id, ordered_content_ids });

export const getMyCourseContentApprovals = params =>
  api.get("/get-my-course-content-approvals", { params });
export const updateCourseContentApproval = (id, status) =>
  api.patch(`/update-course-content-approval/${id}`, { status });

export const updateCourseTestWindow = (course_test_id, begin, end) =>
  api.post("/update-course-test-window", { course_test_id, begin, end });

export const getCourseTestPrereqs = course_id =>
  api.get("/get-course-test-prereqs", { params: { course_id } });

export const saveCourseTestPrereqs = (course_id, target_course_test_id, require_course_test_ids, min_required) =>
  api.post("/save-course-test-prereqs", {
    course_id,
    target_course_test_id,
    require_course_test_ids,
    min_required
  });

export const getCourseUsers = course_id =>
  api.get("/get-course-users", { params: { course_id } });
export const mapCourseUsers = (course_id, user_ids) =>
  api.post("/map-course-users", { course_id, user_ids });
export const unmapCourseUsers = (course_id, user_ids) =>
  api.post("/unmap-course-users", { course_id, user_ids });

export const getCourseApprovers = course_id => api.get("/get-course-approvers", { params: { course_id } })
export const setCourseApprovers = (course_id, user_ids) => api.post("/set-course-approvers", { course_id, user_ids })

export const getCoursesSimple = (params = {}) => api.get("/get-courses-simple", { params })

export const getAdminsForApprover = (params) => api.get("/get-admins-for-approver", { params })
export const getPendingApprovalsCount = () => api.get("/pending-approvals-count")

export const bulkSetCourseApprovers = (course_ids, user_ids) => api.post("/bulk-set-course-approvers", { course_ids, user_ids })

export const getTests = (params = {}) =>
  api.get("/tests", {
    params: {
      status: params.status_ ?? params.status,
      search: params.search,
      contentCode: params.contentCode,
    },
  });
export const getTestsByContent = (contentId, params = {}, name) =>
  api.get(`/contents/${contentId}/tests`, {
    params: {
      status: params.status_ ?? params.status,
      name_test: params.name,
    },
  });
export const createTest = payload => api.post("/create-test", payload);

export async function updateTest(payload) {
  try {
    await api.post("/update-test", payload);
    return { status: "ok" };
  } catch (err) {
    const errCode = err.response?.data?.error ?? "";
    const badReqMap = {
      missing_test_id: "ไม่พบรหัสชุดข้อสอบ (test_id)",
      missing_name: "กรุณาระบุชื่อชุดข้อสอบ",
      missing_question_ids: "กรุณาเลือกคำถามอย่างน้อย 1 ข้อ",
      invalid_question_ids: "รูปแบบรายการคำถามไม่ถูกต้อง",
      invalid_exam_order: "รูปแบบการเรียงข้อสอบไม่ถูกต้อง",
      invalid_option_order: "รูปแบบการเรียงตัวเลือกไม่ถูกต้อง",
      invalid_datetime: "รูปแบบวัน-เวลาไม่ถูกต้อง",
      end_before_start: "วัน-เวลาสิ้นสุดต้องอยู่หลังวัน-เวลาเริ่ม",
      invalid_criteria: "เกณฑ์ผ่านต้องอยู่ระหว่าง 1-100",
      invalid_duration: "ระยะเวลาสอบต้องมากกว่า 0 นาที",
      unauthorized: "ไม่มีสิทธิ์ดำเนินการ",
    };
    const msg =
      {
        400: badReqMap[errCode] || "ข้อมูลไม่ถูกต้อง",
        404: "ไม่พบชุดข้อสอบ",
        409:
          {
            inactive: "ชุดข้อสอบถูกปิดใช้งาน แก้ไขไม่ได้",
            locked: "มีผู้ทำข้อสอบแล้ว ชุดข้อสอบถูกล็อก",
          }[errCode] || "ชุดข้อสอบถูกล็อก",
      }[err.response?.status] || "เกิดข้อผิดพลาด";
    throw new Error(msg);
  }
}

export const getTestList = status => api.get("/tests", { params: { status } });
export const getTestById = (test_id, params = {}) =>
  api.get(`/tests/${test_id}`, {
    params: {
      status: params.status
    }
  });
export const getTestDetail = (test_id, org_id, course_id) =>
  api.get("/get-test-detail", { params: { test_id, org_id, course_id } });
export const toggleExamStatus = (test_id, active) =>
  api.post("/toggle-exam-status", { test_id, active });

export const getSubmissionDetail = (orgId, submitId) =>
  api.get("/submission-detail", { params: { org_id: orgId, submit_id: submitId } });

export const startLearnerRun = (payload) => api.post("/app-test-run/start", payload)
export const getLearnerExamOverview = (params) => api.get("/app-exam/overview", { params })
export const getLearnerQuestions = (params) => api.get("/app-exam/questions", { params })
export const submitLearnerExam = (body) => api.post("/app-exam/submit", body)

export const getLearnerSubmissionDetail = (submissionId) =>
  api.get(`/app-exam/submission/${encodeURIComponent(submissionId)}`);


export const getExamOverview = params => api.get("/overview", { params });
export const getTestRunDetail = params => api.get("/test-run-detail", { params });
export const getPublicQuestions = params => api.get("/public-exam/questions", { params });
export const submitPublicExam = data => api.post("/public-exam/submit", data);
export const getExamRanking = params => api.get("/exam-result-ranking", { params });

export const createPlayerRun = payload =>
  api.post("/create-player-run", payload).then(r => r.data);

export const updatePlayerRun = payload =>
  api.post("/update-player-run", payload).then(r => r.data);

export const updatePlayerRunStatus = (run_id, action) =>
  api.post("/update-player-run-status", { run_id, action }).then(r => r.data);

export const getExportPreview = (org_id, test_id, format) =>
  api
    .get("/export-preview", {
      params: { org_id, test_id, format },
      responseType: "blob",
    })
    .then(r => r.data);

export const exportFile = (org_id, test_id, format) =>
  api
    .get("/export-file", {
      params: { org_id, test_id, format },
      responseType: "blob",
    })
    .then(r => r.data);

export const getClassAppMembers = (org_id, class_id, filters = {}) =>
  api.get("/get-class-app-members", { params: { org_id, class_id, ...filters } });

export const getGroups = () => api.get("/get-groups");
export const saveGroup = payload => api.post("/save-group", payload);
export const deleteGroup = group_id => api.post("/delete-group", { group_id });
export const getGroupMembers = (group_id, params = {}) =>
  api.get("/get-group-members", { params: { group_id, ...params } });
export const addGroupMember = (payload) => api.post("/add-group-member", payload);
export const removeGroupMember = (group_id, learner_id) =>
  api.post("/remove-group-member", { group_id, learner_id });

export const bulkAddGroupMembers = (group_id, file, cfg = {}) => {
  const fd = new FormData();
  fd.append("group_id", group_id);
  fd.append("file", file);
  return ax.post(`${API_BASE}/bulk-add-group-members`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    ...cfg,
  });
};

export const getLearners = (params = {}) => api.get("/get-learners", {
  params: {
    status: params.status,
    group_id: params.group_id,
    job_title: params.job_title,
    searchText: params.searchText,
    page: params.page ?? 1,
    per_page: params.per_page ?? 20,
    sort_by: params.sort_by ?? "created_at",
    sort_dir: params.sort_dir ?? "desc",
    exclude_group_id: params.exclude_group_id,
    exclude_class_id: params.exclude_class_id
  }
})

export const getReportLearnerOverview = (learner_id) =>
  api.get(`/report/learner/${learner_id}`)

export const getReportLearnerCourse = (learner_id, course_id) =>
  api.get(`/report/learner/${learner_id}/course/${course_id}`)

export const getReportLearnerSubmission = (learner_id, submission_id) =>
  api.get(`/report/learner/${learner_id}/submission/${submission_id}`)

export const addLearner = payload => api.post("/add-learner", payload);
export const addLearnerGuest = (token, payload) =>
  api.post(`/add-learner-guest/${token}`, payload);
export const updateLearner = payload => api.post("/update-learner", payload);
export const deleteLearner = (learner_id, hard = false) =>
  api.post("/delete-learner", { learner_id, hard });
export const approveLearner = (learner_id, approver) => api.post("/approve-learner", { learner_id, approver });
export const resendInvite = (learner_id) => api.post("/resend-invite", { learner_id });

export const bulkAddLearnersJson = (rows, created_by, group_id) =>
  api.post("/bulk-add-learners-json", { rows, created_by, group_id });

export const bulkAddLearners = (file, created_by, cfg = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("created_by", created_by);
  return ax.post(`${API_BASE}/bulk-add-learners`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    ...cfg,
  });
};


export const fetchAppMe = () => api.get("/app-me");
export const fetchLearnerOverview = () => api.get("/app-overview");
export const getLearnerCourses = (params = {}) => api.get("/app-my-courses", { params });
export const getLearnerUpcoming = () => api.get("/app-upcoming");
export const getLearnerContentTests = (contentId, params = {}) => api.get(`/app/${contentId}/tests`, { params });
export const getLearnerBadges = () => api.get("/app-badges");
export const updateLearnerProfile = (payload) => api.post("/app-profile", payload);
export const logoutLearner = () => api.post("/app-logout");
export const uploadLearnerAvatar = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/app-profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const getLearnerAvailable = (params = {}) =>
  api.get("/app-available-tests", { params });

export const syncLearnerFromUser = (user_id, enable) =>
  api.post('/sync-learner-from-user', { user_id, enable })


export const createLearnerInvite = (created_by, quota = 0, expire_hours = 48, group_id) =>
  api.post("/create-learner-invite", { created_by, quota, expire_hours, group_id });
export const fetchInviteInfo = token => api.get(`/invite-info/${token}`);
export const fetchInviteInfoGuest = token => api.get(`/invite-info-guest/${token}`);
export const checkLearnerExists = email =>
  api.get("/check-learner-exists", { params: { email } });
export const confirmSignup = (token, payload) => api.post(`/confirm/${token}`, payload);
export const confirmJoin = (token, payload) => api.post(`/confirm-join/${token}`, payload);

export const getJobTitles = status =>
  api.get("/meta/job-titles", { params: { status } });
export const getPrefixes = () => api.get("/meta/prefixes");
export const getCountries = () => api.get("/meta/countries");

export const getorgInfo = () => api.get("/org-info");
export const updateorgInfo = formData => api.post("/org-info", formData, { headers: { "Content-Type": "multipart/form-data" } });

export const getAdminUsers = (params = {}) =>
  api.get('/admin-users', {
    params: {
      search: params.search,
      status: params.status,
      role: params.role,
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      sort_by: params.sort_by ?? 'created_at',
      sort_dir: params.sort_dir ?? 'desc',
      exclude_course_id: params.exclude_course_id,
      exclude_ids: params.exclude_ids
    }
  })

export const createAdminInvite = ({ email, role, first_name, last_name, phone_code, phone, country }) =>
  api.post('/create-admin-invite', { email, role, first_name, last_name, phone_code, phone, country });

export const updateAdminStatus = (user_id, status) =>
  api.post('/update-admin-status', { user_id, status });
export const updateAdminRole = (user_id, role) =>
  api.post('/update-admin-role', { user_id, role_code: role });

export const updateAdmin = (user_id, payload) =>
  api.put(`/update-admin/${user_id}`, payload);


export const getMyProfile = () => api.get('/my-profile');
export const updateMyProfile = (fd) => api.post('/my-profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

export const downloadUpload = rel =>
  `${API_BASE.replace("/examapi", "")}/uploads/${encodeURIComponent(rel)}`;

/**
 * Fetch an attachment file via the authenticated axios instance (with cookies/session).
 * Returns a blob so we can create an object URL and open it without React Router interception.
 */
export const fetchAttachmentFile = (attachmentId) =>
  api.get(`/attachment-file/${encodeURIComponent(attachmentId)}`, {
    responseType: 'blob',
  });

// ─── Content Detail ───────────────────────────────────────────────────────────
export const getContentDetail = (contentId) =>
  api.get("/content-detail", { params: { contentId } });

export const getAppContentDetail = (contentId) =>
  api.get("/app-content-detail", { params: { contentId } });

export const getAppLessons = params => {
  const p = {
    contentId: params?.contentId || params?.content_id,
    contentCode: params?.contentCode || params?.content_code,
    level: params?.level,
    examType: params?.examType,
    searchText: params?.searchText || params?.search,
    page: params?.page,
    pageSize: params?.pageSize
  };
  return api.get("/app-lessons", { params: p });
};

export const createContentDetail = (payload) =>
  api.post("/content-detail/create", payload);

export const updateContentDetail = (payload) =>
  api.post("/content-detail/update", payload);

// ─── Subscription ─────────────────────────────────────────────────────────────
export const getSubscription = () => api.get("/subscription");
export const getPlans = () => api.get("/plans");
export const upgradeSubscription = (payload) => api.post("/subscription/upgrade", payload);
export const renewSubscription = (payload) => api.post("/subscription/renew", payload);
export const cancelSubscription = (payload) => api.post("/subscription/cancel", payload);
export const getSubscriptionHistory = () => api.get("/subscription/history");
export const getSubscriptionLimits = () => api.get("/subscription/limits");

export const generatePromptPayQR = (payload) => api.post("/payment/promptpay/qr", payload).then(r => r.data);

export default api;
